import type { paths } from "../../api/generated/api-types";
import {
  INITIAL_STRUCTURE_PARAMETERS,
  type SharedFormFields,
  type UrlGroup,
  type RelevantTopicAnchorRow,
  type TaskType,
  type PublicationParams,
} from "./useTaskCreation";
import { PUBLICATION_FILTER_DEFAULTS } from "../shared/publicationFilterDefaults";

// The clone source is the TASK-03 initial snapshot already held by TaskDetail.
// Derived locally from the generated contract (see TaskDetail/useTaskDetailSubscription.ts
// for the same derivation) so the mapper never depends on the TaskDetail screen.
type TaskDetailResponse =
  paths["/tasks/{taskId}"]["get"]["responses"]["200"]["content"]["application/json"];

type TaskInfo = TaskDetailResponse["task"];
type AnchorInfo = TaskDetailResponse["anchors"][number];
type ArticleTopicInfo = TaskDetailResponse["articleTopics"][number];

export type CloneFrom = {
  task: TaskInfo;
  anchors: AnchorInfo[];
  articleTopics: ArticleTopicInfo[];
};

type CloneAnchorState = Pick<UrlGroup, "anchorsText" | "uniqueArticlesCount" | "anchorInputMode">;

/**
 * Pure mapper: TASK-03 snapshot → TaskCreation form state, prefilled in clone mode.
 * `defaultTopicPicker` supplies a processing-default general topic whenever a snapshot
 * topic is null (screen doc «Клонирование»: null → стандартный дефолт обработки). It is
 * injected because the default requires the loaded topics dictionary, which this pure
 * mapper does not fetch; the hook passes the same random picker used when processing.
 */
export function buildCloneFormState(
  cloneFrom: CloneFrom,
  defaultTopicPicker: () => string,
): { shared: SharedFormFields; groups: UrlGroup[] } {
  const { task, anchors, articleTopics } = cloneFrom;
  const shared: SharedFormFields = {
    taskType: task.taskType,
    language: task.language,
    region: task.region,
    autoPublish: task.autoPublish,
    structureParams: task.structureParams ?? INITIAL_STRUCTURE_PARAMETERS,
    publicationParams: normalizeClonedPublicationParams(task.publicationParams),
  };
  const anchorState = buildCloneAnchorState(task.taskType, anchors, articleTopics, defaultTopicPicker);
  const group: UrlGroup = {
    targetUrl: task.targetUrl,
    comment: task.comment ?? "",
    anchorsText: anchorState.anchorsText,
    uniqueArticlesCount: anchorState.uniqueArticlesCount,
    anchorInputMode: anchorState.anchorInputMode,
  };
  return { shared, groups: [group] };
}

/**
 * Screen doc «Клонирование»: task.publicationParams null → block's initial values (PUB-01
 * defaults). A non-null snapshot with `regions = null` always carries secondary sites
 * (screen doc «Параметры публикации»: the checkbox renders checked and locked while «Все
 * регионы» is on) — the same invariant useTaskCreation.setPublicationParameter restores on
 * every interactive change, so the initial clone prefill restores it too rather than
 * trusting the snapshot as-is.
 */
function normalizeClonedPublicationParams(
  publicationParameters: TaskInfo["publicationParams"],
): PublicationParams {
  let normalized: PublicationParams;
  if (publicationParameters === null) {
    normalized = PUBLICATION_FILTER_DEFAULTS;
  } else if (publicationParameters.regions === null) {
    normalized = { ...publicationParameters, includeSecondary: true };
  } else {
    normalized = publicationParameters;
  }
  return normalized;
}

/**
 * Full-format lines so «Вернуться к textarea» round-trips; the parser peels fields
 * from the right, so commas inside anchor text stay safe (parseRelevantTopicLine).
 */
export function buildFullFormatAnchorsText(rows: RelevantTopicAnchorRow[]): string {
  return rows
    .map((row) => `${row.text},${row.articleCount},${row.uniqueness},${row.generalTopic}`)
    .join("\n");
}

function buildCloneAnchorState(
  taskType: TaskType,
  anchors: AnchorInfo[],
  articleTopics: ArticleTopicInfo[],
  defaultTopicPicker: () => string,
): CloneAnchorState {
  const textareaLines = anchors.map((anchor) => `${anchor.anchorText},${anchor.articleCount}`).join("\n");
  let cloneAnchorState: CloneAnchorState;

  switch (taskType) {
    case "generation_anchor_topic":
    case "homepage_publication":
    case "sitewide_publication":
      cloneAnchorState = {
        anchorsText: textareaLines,
        uniqueArticlesCount: 1,
        anchorInputMode: { mode: "textarea" },
      };
      break;
    case "generation_relevant_topic": {
      const rows: RelevantTopicAnchorRow[] = anchors.map((anchor) => ({
        text: anchor.anchorText,
        articleCount: anchor.articleCount,
        uniqueness: anchor.uniqueness ?? 5,
        generalTopic: anchor.generalTopic ?? defaultTopicPicker(),
      }));
      cloneAnchorState = {
        anchorsText: buildFullFormatAnchorsText(rows),
        uniqueArticlesCount: 1,
        anchorInputMode: { mode: "table", rows },
      };
      break;
    }
    case "generation_relevant_topic_fixed":
      cloneAnchorState = {
        anchorsText: textareaLines,
        uniqueArticlesCount: articleTopics.length,
        anchorInputMode: {
          mode: "fixed-topics",
          topics: articleTopics.map((topic) => topic.generalTopic ?? defaultTopicPicker()),
        },
      };
      break;
  }

  return cloneAnchorState;
}
